/* eslint-disable sonarjs/no-duplicate-string */
import { Router, ActivatedRoute, Params } from '@angular/router';
import { WindowMessageService } from '../messaging/window-message.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppConfig } from 'src/app/app.config';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { ApiSelectorService } from '../api-selector/api-selector.service';
import { StorageHelper } from '../storage/storage-helper';

@Component({
  selector: 'app-hickory-single-page',
  templateUrl: './hickory-single-page.component.html',
  styleUrls: ['./hickory-single-page.component.scss'],
  standalone: false
})
export class HickorySinglePageComponent implements OnInit, OnDestroy {
  url: string;
  params: Params = this.route.snapshot.queryParams;
  redirect: string = this.route.snapshot.queryParams?.redirect ?? null;
  redirectOnLoad: boolean = this.route.snapshot.queryParams?.redirectOnLoad ?? false;

  lastHickoryPath: string;
  hickoryUrl: SafeResourceUrl;
  isLoading = true;

  subscription = new Subscription();

  constructor(
    private sanitizer: DomSanitizer,
    private messageService: WindowMessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiSelectorService,
  ) {}

  ngOnInit(): void {
    const hickoryHost = this.apiService.getValue(AppConfig.hosts.hickoryEmbed || 'http://localhost:51212');
    this.url = this.route.snapshot.data.url;
    this.hickoryUrl = this.sanitizer.bypassSecurityTrustResourceUrl(hickoryHost + this.url);
    const paramString = Object.keys(this.params)
      ?.map((key) => `${key}=${this.params[key]}`)
      ?.join('&');

    this.setEmbeddedUrl(hickoryHost, this.url?.concat('?', paramString));

    this.subscription.add(
      this.messageService.lastMessage.subscribe((msg) => {
        if (msg && msg.data && msg.type === 'konexus.embedded' && msg.data.meta) {
          // Accept the message (removes from queue)
          this.messageService.acceptMessage(msg);
          // Handle the message
          this.processEmbeddedPageData(msg.data.meta);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  processEmbeddedPageData(meta: { [attribute: string]: any }) {
    const href = meta['href'];
    if (!href) return;

    const embedPath = this.getEmbedPath(href);
    this.lastHickoryPath = embedPath;
    const redirect = this.redirect?.split('?')[0];
    const redirectParams = this.redirect?.split('?')[1]?.split('&').reduce((acc, curr) => {
      const [key, value] = curr.split('=');
      acc[key] = value;
      return acc;
    }, {});

    if (this.redirect?.includes('groups')) {
      const classification: string = this.route.snapshot.queryParams?.classification ?? null;

      switch (embedPath) {
        case '/edit/Groups.aspx':
          this.router.navigate([redirect], { queryParams: redirectParams });
          return;
        case '/edit/loginMaint.aspx':
          if (classification) {
            this.router.navigate(['/contacts', classification?.toLowerCase()]);
          } else {
            this.router.navigate(['/contacts', 'internal']);
          }
          return;
        default:
          break;
      }

      if (embedPath.includes('loginMaintForm.aspx')) {
        const id = meta['href'].split('id=')[1];
        // set the router url without triggering a navigation change
        const goLocation = `/contacts/edit?id=${id}`;
        this.location.go(goLocation);
        this.isLoading = false;
        return;
      }
    }

    if (this.redirect?.includes('contacts') && embedPath === '/edit/loginMaint.aspx') {
      this.router.navigate([redirect], { queryParams: redirectParams });
      return;
    }

    // If all else fails, we want to route to the new pages if we end up on the old pages.
    switch (embedPath) {
      case '/edit/loginMaint.aspx':
        this.router.navigate(['/contacts', 'internal']);
        return;
      case '/edit/Groups.aspx':
        this.router.navigate(['/groups', 'internal']);
        return;
      default:
        break;
    }

    this.isLoading = false;
  }

  getEmbedPath(uri: string) {
    const parser = document.createElement('a');
    parser.href = uri;
    return parser.pathname + parser.search;
  }

  setEmbeddedUrl(host: string, relativeUrl: string) {
    StorageHelper.saveIsEmbedUxForHost(host);
    this.hickoryUrl = this.sanitizer.bypassSecurityTrustResourceUrl(host + relativeUrl);
  }

  onLoadEvent() {
    this.isLoading = !this.lastHickoryPath;

    if (this.redirectOnLoad) {
      this.router.navigate([this.redirect]);
    }
  }
}
